import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { InfiniteScrollCustomEvent, IonContent } from '@ionic/angular';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  filter,
  forkJoin,
  merge,
  Observable,
  of,
  skip,
  startWith,
  Subscription,
  switchMap,
  take,
  tap,
  throttleTime,
  timer,
} from 'rxjs';

import { LabelsEnum, NotificationStatus } from '@shared/sms/enums';
import { WebSocketEventsEnum } from '@shared/websocket';
import { Schedule } from '@shared/schedule/models';
import { Resource } from '@shared/resource/models';
import { Service } from '@shared/service/models';
import { AuthUser } from '@shared/signup/models';
import { Location } from '@shared/location/models';
import { PartySource, PartyTypes } from '@shared/party/enums';
import { Party } from '@shared/party/models';
import { UserLocationRole } from '@shared/user/enums';
import { FieldType } from '@shared/restaurant/enums';
import { CheckoutStatus } from '@shared/stripe/enums';
import { IParty, OrderByValues, PageTitleService, SearchBarComponent } from '@tablesready/common-ui';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { HeaderTitleService } from '@tablesready/frontend-header';
import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';
import {
  AlertsService,
  ClockService,
  IAlertTypes,
} from '@tablesready/frontend-alerts-bar';
import {
  GuidedTourStep,
  GuidedTourStepStrings,
  GuiderService,
} from '@tablesready/frontend-guider';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import { AuthService } from '@tablesready/frontend-auth';
import { FrontendBreakpointsService } from '@tablesready/frontend-breakpoints';
import {
  PricePlansRestrictionsService,
  PricePlansRestrictionsModalService,
  MarkPartyService,
  MessageButtonService,
  MessageSenderService,
  PartiesService,
  ReservationService,
  ResourcesService,
  ScheduleService,
  ServicesService,
  WaitlistService,
  WaitTimeService,
} from '../services';
import { EditResourcesComponent, WaitlistAddEditPartyComponent } from './components';
import {
  ChatPartyComponent,
  NotifyActions,
  WaitlistActionsCallbacks,
} from '../components';
import { IWaitingParty, IWaitlistPartiesInputData } from './models';

const msPerMin = 60000;

@Component({
  selector: 'tablesready-waitlist',
  templateUrl: './waitlist.component.html',
  styleUrls: ['./waitlist.component.scss'],
  animations: [
    trigger('markParty', [
      state('expanded', style({ opacity: 1 })),
      state('collapsed', style({ opacity: 0 })),
      transition('expanded => collapsed', [animate('200ms ease-out')]),
    ]),
  ],
  providers: [MessageButtonService],
})
export class WaitlistComponent implements OnInit, OnDestroy {
  @ViewChild(IonContent) ionContent?: IonContent;
  @ViewChild('searchBarRef') searchBarRef?: SearchBarComponent;
  @ViewChild('modalRef', {
    read: WaitlistAddEditPartyComponent,
    static: true,
  }) modalRef?: WaitlistAddEditPartyComponent;

  icon = AppIcon;
  size = AppIconSize;
  color = AppIconColor;

  public now: number;
  public timezone: string;

  public sortBy: string = 'quoted_time';
  public waitFields
  public showAllDates;
  public phoneNumber;
  public displayCompact;
  public disableScroll: boolean;

  public loading: boolean;
  public waitingParties: Array<IWaitingParty>;
  public user: AuthUser;
  public location: Location;
  public userLocationRole: UserLocationRole;
  public hasViewedGuidedTour: boolean = false;
  public waitlist_search_query: string = '';
  public sortReverse: boolean = false;
  public parties: Party[] = [];
  public currentParty: IParty;
  public waitValues: Array<number> = [0, 0, 0, 0, 0];
  public showEstimatedWait = false;
  public onlyOneWaitValue: boolean = true;
  public noEstimatedWaitMessage: string = 'No wait';
  public partyCount: number = 0;
  public guestCount: number = 0;
  public page: number = 1;
  public limit: number = 40;
  public schedule: Schedule[];
  public resources: Resource[] = [];
  public services: Service[] = [];
  public currentSizeFilter: string | null = null;
  public isCurrentPartiesFilterActive: boolean = false;
  fieldTypes = FieldType;
  partyTypes = PartyTypes;
  partySources = PartySource;
  actionsCallbacks?: WaitlistActionsCallbacks<IParty>;
  notifyActions?: NotifyActions<Party>;
  isMobile = false;
  isHandset = false;
  private subscription = new Subscription();
  private ionSubscription = new Subscription();
  private waitlistPartiesData = new BehaviorSubject<IWaitlistPartiesInputData>({
    page: this.page,
    sortBy: this.sortBy,
    sortReverse:this.sortReverse,
    limit: this.limit,
    search: this.waitlist_search_query,
  });

  private availableForStarterPlan = false;
  private availableForBusinessPlan = false;
  private debouncePartiesMs = 200;
  private stopUpdateWaits: Subscription;
  private infiniteScrollEvent?: InfiniteScrollCustomEvent;

  constructor(
    readonly guiderService: GuiderService,
    readonly messageButtonService: MessageButtonService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly clockService: ClockService,
    private readonly resourcesService: ResourcesService,
    private readonly partiesService: PartiesService,
    private readonly socketService: SocketService,
    private readonly alertsService: AlertsService,
    private readonly markPartyService: MarkPartyService,
    private readonly modalController: FrontendModalService,
    private readonly servicesService: ServicesService,
    private readonly waitTimeService: WaitTimeService,
    private readonly messageSenderService: MessageSenderService,
    private readonly service: WaitlistService,
    private readonly pageTitleService: PageTitleService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly scheduleService: ScheduleService,
    private readonly headerService: HeaderTitleService,
    private readonly reservationService: ReservationService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly frontendBreakPointObserver: FrontendBreakpointsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    @Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables,
  ) {}

  onIonInfinite(ev: InfiniteScrollCustomEvent): void {
    this.infiniteScrollEvent = ev;
    this.loadMore();
  }

  ionViewWillEnter(): void {
    void this.ionContent?.scrollToTop(0);
    this.ionSubscription = new Subscription();

    const accountSub = this.authRestaurantService.selectWithAccount()
      .pipe(
        switchMap(user => combineLatest([
          of(user),
          this.resourcesService.selectAll().pipe(tap(resources => this.resources = resources)),
          this.servicesService.selectAll().pipe(tap(services => this.services = services)),
        ])),
        tap(([user]) => {
          this.user = user;
          this.location = user.location;
          this.timezone = this.location.timezone;
          this.availableForStarterPlan = this.pricePlansRestrictionsService.availableForStarterPlan(this.user.account.subscriptionType, this.location.recurly);
          this.availableForBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(this.user.account.subscriptionType, this.location.recurly);
          this.userLocationRole = this.user.locationUser.role;
          this.updateUserSettings(this.location);
          this.updateParties(this.parties);

          this.hasViewedGuidedTour = typeof user.locationUser.hasViewedGuidedTour === 'undefined' ||
            user.locationUser.hasViewedGuidedTour === null ? true : user.locationUser.hasViewedGuidedTour;

          this.headerService.setDataForRoute('waitlist', { title: this.location.name + ' Waitlist' });
          this.emitUpdateWaitlistPartiesEvent();
        }),
      )
      .subscribe();
    this.ionSubscription.add(accountSub);

    const dataSub = this.waitlistPartiesData.asObservable()
      .pipe(
        startWith(null),
        debounceTime(this.debouncePartiesMs),
        concatMap(options => forkJoin([this.updateWaitlistParties(options), of(options)])),
        tap(([data, options]) => {
          const { parties, count, waitingPartiesData } = data;

          if (this.infiniteScrollEvent) {
            void this.infiniteScrollEvent.target.complete();
            this.infiniteScrollEvent = null;
          }

          if (!parties.length && options.page !== 1) {
            this.disableScroll = true;
            this.loading = false;
            return false;
          }
          //get lodash
          this.updateParties(parties, options.page);
          this.waitingParties = waitingPartiesData?.waitingParties || false;
          this.guestCount = waitingPartiesData?.guestCount || 0;
          this.updateWaitingParties();
          this.loading = false;
          this.setPartyCount(count);
          this.disableScroll = false;
          this.page = options.page + options.limit / this.limit;
          this.sendNextInLineMessage();
        }),
      )
      .subscribe();
    this.ionSubscription.add(dataSub);
  }

  ionViewWillLeave(): void {
    this.resetFilters();
    if (!this.searchBarRef) {
      return;
    }

    this.searchBarRef.clearSearchField();
    this.ionSubscription.unsubscribe();
  }

  updateSearchQuery(query: string): void {
    this.waitlist_search_query = query;
    this.page = 1;
    this.emitUpdateWaitlistPartiesEvent();
  }

  filterBySize(sizeGroup: string | null): void {
    // If clicking the same size filter that's already active, deactivate it
    if (sizeGroup === null || sizeGroup === this.currentSizeFilter) {
      this.currentSizeFilter = null;
    } else {
      this.isCurrentPartiesFilterActive = false;
      this.currentSizeFilter = sizeGroup;
    }

    this.page = 1;
    this.emitUpdateWaitlistPartiesEvent();
  }

  public get getUndoMessage() {
    if (this.currentParty) {
      return `${this.currentParty.name} ${this.location.displayOptions?.labels?.customer || 'customer'} of ${this.currentParty.size}.`;
    }
  }

  ngOnInit(): void {
    this.pageTitleService.setTitle('Waitlist');
    this.actionsCallbacks = {
      addEditParty: (party: Party): void => {
        this.addEditParty(party);
      },
      checkPartyResources: (party: Party): void => {
        this.checkPartyResources(party);
      },
      confirmDelete: (party: Party): void => {
        this.confirmDelete(party);
      },
      chatParty: (party: Party, index: number): void => {
        this.chatParty(party, index);
      },
    };

    this.notifyActions = {
      smsPage: (party): void => {
        this.messageButtonService.sendReadySms(party, this.location.messages.smsNext.message);
      },
      disableMessageButton: (party): boolean => {
        return this.messageButtonService.disableMessageButton(party);
      },
      mergeInboundOutbound: party => this.messageButtonService.mergeInboundOutbound(party),
      voicePage: (party): void => {
        this.voicePage(party);
      },
    };

    const breakPointObserverSub = this.frontendBreakPointObserver.observeXsBreakpoint()
      .pipe(tap(isMobile => this.isMobile = isMobile)).subscribe();
    this.subscription.add(breakPointObserverSub);

    const handsetSub = this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.HandsetLandscape])
      .pipe(tap(({ matches: isHandset }) => this.isHandset = isHandset)).subscribe()
    this.subscription.add(handsetSub);

    const clockSub = this.clockService.clock$.subscribe(now => this.now = now);
    this.subscription.add(clockSub);

    //sockets
    const partiesStream = this.socketService.getEventStream(WebSocketEventsEnum.parties);
    const TextSmsEventStream = this.socketService.getEventStream(WebSocketEventsEnum.TextSmsEvent);
    const VoiceSmsEventStream = this.socketService.getEventStream(WebSocketEventsEnum.VoiceSmsEvent);
    const InboundSmsEventStream = this.socketService.getEventStream(WebSocketEventsEnum.InboundSmsEvent);
    const displayOptionsStream = this.socketService.getEventStream(WebSocketEventsEnum.displayOptions);
    const reservationSettingsStream = this.socketService.getEventStream(WebSocketEventsEnum.reservationSettings);

    const socketsSub = merge(
      partiesStream,
      TextSmsEventStream,
      VoiceSmsEventStream,
      InboundSmsEventStream,
      displayOptionsStream,
      reservationSettingsStream,
    )
      .pipe(
        throttleTime(this.debouncePartiesMs),
        tap((res) => {
          const statusPage = Math.ceil(this.parties.length / this.limit) || 1;
          this.partiesEventHandler(statusPage);
        }),
      )
      .subscribe();
    this.subscription.add(socketsSub);

    const scheduleSub = this.scheduleService.selectAll()
      .pipe(
        tap((schedule) => this.schedule = schedule),
        catchError(() => {
          this.alertsService.addError('Please, configure business hours', 'ERROR:');

          return of(null);
        }),
      ).subscribe();
    this.subscription.add(scheduleSub);
  }

  onNext(step?: GuidedTourStepStrings) {
    if(step === GuidedTourStep.AddEditParty) {
      this.addEditParty();
    }
  }

  onPrev(step?: GuidedTourStepStrings) {
    if(step === GuidedTourStep.AddEditParty) {
      this.addEditParty();
    }
  }

  emitUpdateWaitlistPartiesEvent(page = 1, statusPage: number = 1): void {
    const isAuthenticated = this.authService.isAuthenticated();

    if (isAuthenticated) {
      const initOptions: IWaitlistPartiesInputData = {
        page,
        sortBy: this.sortBy,
        sortReverse: this.sortReverse,
        limit: this.limit * statusPage,
        search: this.waitlist_search_query,
        sizeGroup: this.currentSizeFilter,
      };
      this.waitlistPartiesData.next(initOptions);
    }
  }

  partiesEventHandler(statusPage: number) {
    this.page = 1;
    if (this.loading) {
      return;
    }
    this.disableScroll = false;
    this.emitUpdateWaitlistPartiesEvent(1, statusPage);
  }

  private setLoadingState(): void {
    this.parties = [];
    this.disableScroll = false;
    this.loading = true;
  }

  orderBy({ orderBy, orderAscending }: OrderByValues): void {
    this.sortReverse = orderAscending;
    this.sortBy = orderBy;
    this.setLoadingState();
    this.emitUpdateWaitlistPartiesEvent();
  }

  updateUserSettings(user: Location): void {
    this.sortBy = user.waitListSortBy;
    this.waitFields = user.waitFields
      ? user.waitFields
      : {
          quoted_time: true,
          wait_time: true,
          arrival_time: false,
          size: true,
          estimated_waits: false,
          note_column: false,
          position_in_line: false
        };
    this.waitFields = {
      ...this.waitFields,
      estimated_waits: this.waitFields?.estimated_waits || false,
      note_column: this.waitFields?.note_column || false,
    };
    this.showAllDates = user.displayOptions?.future || false;
    this.phoneNumber = user.phoneNumber;
    if (
      user.noteOptions &&
      !Array.isArray(user.noteOptions) &&
      user.noteOptions !== ''
    ) {
      user.noteOptions = (user.noteOptions as string).split(',');
    }
    this.displayCompact = user.displayOptions?.compact || false;
  }

  loadMore() {
    if (this.loading) {
      return false;
    }

    this.loading = true;
    this.emitUpdateWaitlistPartiesEvent(this.page);
  }

  updateWaitlistParties(options: IWaitlistPartiesInputData): Observable<any> {
    this.loading = true;
    const { page, limit, sortBy, sortReverse, search, sizeGroup } = options;
    const sort = {};
    if (sortBy || this.sortBy) {
      sort[sortBy || this.sortBy] = sortReverse || this.sortReverse ? -1 : 1;
    }

    return this.partiesService.getParties({
      page,
      limit,
      search,
      sort,
      sizeGroup,
    });
  }

 updateParties(parties: Party[], page = 1) {
    if (page === 1) {
      this.parties = parties;
    } else {
      this.parties = this.partiesService.unique([...this.parties, ...parties]);
    }
  }

  updateWaitingParties() {
    if (!this.waitingParties) {
      return;
    }

    if (!this.stopUpdateWaits) {
      this.stopUpdateWaits = timer(0, 5 * msPerMin)
        .pipe(
          //we need it to avoid call 2 times
          skip(1),
          tap(()=> {
            this.updateWaitTimes();
          })
        ).subscribe();

      this.subscription.add(this.stopUpdateWaits);

      return;
    }

    this.updateWaitTimes();
  }

  updateWaitTimes() {
    if (!this.waitingParties) {
      return;
    }

    this.subscription.add(
      this.waitTimeService.getEstimates(this.waitingParties).subscribe(
        (data) => {
          this.waitingParties = data.waitingParties;
          this.waitValues = data.waitValues;
          this.onlyOneWaitValue = data.onlyOneWaitValue;
          this.showEstimatedWait = data.showEstimatedWait;
      },
      (error) => {
        if (error.message === 'error finding estimates in db') {
          this.noEstimatedWaitMessage = 'Not enough data';
        }
        this.stopUpdatingWaitsAction();
      })
    )
  }

   stopUpdatingWaitsAction(): void {
    if (this.stopUpdateWaits) {
      this.stopUpdateWaits.unsubscribe();
      this.stopUpdateWaits = null;
    }
  }

  sendNextInLineMessage() :void{
    if (
      this.location?.messages &&
      this.location?.messages.smsNext &&
      this.location?.messages.smsNext.enabled &&
      this.location?.messages.smsNext.message &&
      this.location?.messages.smsNext.position &&
      this.parties.length >= this.location?.messages.smsNext.position
    ) {
      const checkedInParties = this.parties.filter(p => p.checkin_time);
      if (checkedInParties.length < this.location?.messages.smsNext.position) {
        return;
      }
      const party = checkedInParties[this.location.messages.smsNext.position - 1];

      // only send next in line message if quoted time is less than an hour away
      // and wasn't added within 3 minutes
      // also only send to reservations if checked in more than 3 mins ago
      const maxQuotedTimeMins = 60;
      const elapsedMins = 3;
      const now = new Date().getTime();
      if (
        party &&
        party.phone &&
        party.created_time <= now - elapsedMins * msPerMin &&
        party.quoted_time <= now + maxQuotedTimeMins * msPerMin &&
        (party.type !== this.partyTypes.reservation ||
          party.type === this.partyTypes.reservation &&
            party.checkin_time &&
            party.checkin_time <= now - elapsedMins * msPerMin)
      ) {
        let alreadySent = {};
        const maxAttempts = 3;
        let attempts = 0;
        if (party.notifications) {
          // check if we have already sent or have tried to send one already
          alreadySent = party.notifications.find(notification => notification.label &&
            notification.label === LabelsEnum.next &&
            notification.status_sent !== NotificationStatus.notSent
          );
          if (alreadySent === undefined) {
            // need to stop attempting send if too many failed attempts (of any type)
            const failures = party.notifications.filter(notification =>
              notification.phone === party.phone &&
              notification.type === 'sms' &&
              notification.status_sent === NotificationStatus.notSent
            );
            attempts = failures.length;
          }
        }

        if (!party.notifications || (alreadySent === undefined && attempts < maxAttempts)) {
          this.messageSenderService.smsPage(party, this.location.messages.smsNext.message, LabelsEnum.next);
        }
      }
    }
  }

  public isArray (val: any): boolean {
    return val instanceof Array;
  };

  voicePage (party: IParty) :void {
    this.messageSenderService.voicePage(party, LabelsEnum.ready);
  };

  trackById = (_: number, item: Party): string => item._id;

  checkIn(party: IParty): void {
    this.currentParty = party;
    this.markPartyService.setParty({
      party_id: party._id,
      checkin_time: Date.now(),
    })
      .pipe(catchError((err) => {
        this.alertsService.add({
          type: IAlertTypes.danger,
          heading: 'Error:',
          body: `${this.location.displayOptions?.labels?.customer || 'customer'} check-in failure.`,
        });

        return of(err);
      }))
      .subscribe(() => {
        this.cdr.detectChanges();
        const body = this.alertsService.getTextMessage(
          party.name,
          party.size,
          this.location.displayOptions?.labels?.reservation,
          this.location.displayOptions?.labels?.customer,
        );

        this.alertsService.add({
          type: IAlertTypes.success,
          heading: 'Checked In:',
          body,
        });
      });
  }

  checkPartyResources(party: Party) :void {
    if (!party?.resources?.length && this.location?.bookingOptions?.servingTracking) {
      this.modalController.create<EditResourcesComponent, IParty>(EditResourcesComponent, {
        party,
        location: this.user.location,
        schedule: this.schedule,
        title: 'Assign Services & Resources',
      })
        .pipe(take(1))
        .subscribe((newPartyData) => {
          if (newPartyData) {
            this.markSeated(newPartyData);
          }
        });
    } else {
      this.markSeated(party);
    }
  }

  // Mark Seated
  markSeated(party:IParty): void {
    this.currentParty = party;
    party.isHidden = true;
    // TODO: no servingTraking column in restaurant
    // if (this.user?.servingTraking && !(party.resources || []).length && this.user?.resourceSettings?.enabled) {
    //   return this.addEditParty(party, true);
    // }
    const options = {
      party_id: party._id,
      seated_time: new Date().getTime(),
    };

    this.subscription.add(
      this.markPartyService.setParty(options).subscribe(
        () => {
          this.markPartyAlert(party, 'Served:')
        },
        () => {
          party.isHidden = false;
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `${this.location.displayOptions?.labels?.customer || 'customer'} mark as served failure.`,
          });
        }
      )
    );
  }

  markPartyAlert(party: IParty, heading: string, refunded = false): void {
    let actions = [
      {
        label: 'Undo this',
        action: () => {
          this.markUndo();
        },
      },
    ];

    if (refunded) {
      actions = [];
    }

    this.alertsService.add({
      type: IAlertTypes.success,
      local: true,
      heading,
      body: this.getUndoMessage,
      actions,
    });
    this.setPartyCount(this.partyCount - 1);
  }

  // No-Show
  confirmDelete(party: IParty): void {
    const paid = party.stripeData?.status === CheckoutStatus.paid;

    if (party.type === PartyTypes.reservation && paid) {
      this.reservationService.confirmDelete(party)
        .pipe(take(1))
        .subscribe(({ data }) => {
          this.currentParty = party;
          party.isHidden = true;
          this.markPartyAlert(party, 'Success:', data.performRefund);
        });
    } else {
      this.currentParty = party;
      party.isHidden = true;
      const options = {
        party_id: party._id,
        canceled_time: new Date().getTime(),
      };

    this.subscription.add(
      this.markPartyService.setParty(options).subscribe(
        (response) => {
          const { success, error } = response;
          if (success) {
            this.markPartyAlert(party, 'Canceled:');
              return;
            }

            this.alertsService.addWarning(error, 'Canceled party: ');
          },
          () => {
            party.isHidden = false;
            this.alertsService.addError(`${this.location.displayOptions?.labels?.customer || 'customer'} mark canceled failure.`);
          }
        )
      );
    }
  }

  markUndo () {
    const partyId: string = this.currentParty._id.toString().trim();
    this.alertsService.clear();
    this.markPartyService.setParty({
      party_id: partyId,
      seated_time: null,
      canceled_time: null
    }).subscribe();
  }

  addEditParty(party?: Party, showSelectResourceMessage = false): void {
    this.service.addEditParty(
      this.user,
      this.parties,
      this.schedule,
      undefined,
      false,
      null,
      showSelectResourceMessage,
      undefined,
      party,
    ).subscribe((updatedParty) => {
      this.disableScroll = false;

      if (showSelectResourceMessage) {
        this.markSeated(updatedParty);
      }
    });
  }

  // CHAT PARTY
  chatParty(party: IParty, idx = 0): void {
    if (!this.availableForStarterPlan) {
      this.pricePlansRestrictionsModalService.display(this.user, true).pipe(
        filter(({ isConfirmed }) => isConfirmed),
      ).subscribe();

      return;
    }

    this.modalController.create<ChatPartyComponent, unknown>(ChatPartyComponent, {
      location: this.user.location,
      party: party,
      showTemplates: this.user.account.features.savedReplies,
    })
      .pipe(take(1))
      .subscribe();
  }

  get displayEstimatedWait(): boolean {
    return !!this.waitFields?.estimated_waits && !!this.waitingParties;
  }

  get showCustomFieldsData(): boolean {
    return this.availableForBusinessPlan
      && this.user.location.partyFields
      && this.user.location.partyFields.length > 0;
  }

  ngOnDestroy() {
    this.service.clearModalSubs();
    this.messageButtonService.ngOnDestroy();
    this.stopUpdatingWaitsAction();
    this.subscription.unsubscribe();
  }

  unpaid(party: Party): boolean {
    return party.stripeData?.status === CheckoutStatus.inProgress
      && (party.created_time + this.appVariables.UNPAID_PARTY_LIFETIME * 1000) > Date.now();
  }

  paid(party: Party): boolean {
    return party.stripeData?.status === CheckoutStatus.paid;
  }

  getRemainingSecondsToPay(partyCreatedTime: number): number {
    return partyCreatedTime + (this.appVariables.UNPAID_PARTY_LIFETIME * 1000);
  }

  displayCustomFieldCell(party: Party, customFieldId: string): boolean {
    if (this.isMobile) {
      return party.customFields[customFieldId] !== null && party.customFields[customFieldId] !== undefined;
    }

    return true;
  }

  private setPartyCount(count: number): void {
    this.partyCount = count;
    this.headerService.setDataForRoute('waitlist', { badgeCount: count });
  }

  private resetFilters(): void {
    this.page = 1;
    this.sortReverse = false;
    this.sortBy = 'quoted_time';
    this.waitlist_search_query = '';

    this.updateWaitlistParties({
      page: this.page,
      limit: this.limit,
      sortBy: this.sortBy,
      sortReverse: this.sortReverse,
      search: this.waitlist_search_query,
    });
  }
}
